import type { DocumentTypeName } from '../shared/desktop-api';

type NamedType = { id: string; name: string };
type Filed = { documentType: string; documentTypeId?: string | null };
const key = (name: string) => name.trim().toLocaleLowerCase();

/**
 * The job's type a document counts as, by identity, or undefined when it counts as none and the
 * person chooses one. A type rename relabels only the open batch, so a document read before it (or
 * named by a lesson learned before it) still carries the old name: it is the same type, shown and
 * confirmed under the current name.
 *
 * The type id decides: the document is its id's type when it carries that type's name or a name
 * the type has had (`names`, the job's saved history). A name that type never carried (a lesson's
 * name, another type's name) is no type. Without a live id: the type with that name, or the one
 * surviving type that ever carried it. A deleted type's name is no type. Until the history is read
 * (`names` null), only a document's current name counts. The engine confirms by the same rule
 * (LearnedTypeNames.TypeOf).
 */
export function filedDocumentType<T extends NamedType>(
  document: Filed,
  types: readonly T[],
  names: readonly DocumentTypeName[] | null,
): T | undefined {
  const stored = key(document.documentType ?? '');
  if (!stored) return undefined;
  const carried = (id: string) =>
    (names ?? []).some((entry) => entry.id === id && key(entry.name) === stored);
  const current = document.documentTypeId
    ? types.find((type) => type.id === document.documentTypeId)
    : undefined;
  if (current) return key(current.name) === stored || carried(current.id) ? current : undefined;
  const live = types.find((type) => key(type.name) === stored);
  if (live) return live;
  const carriers = new Set(
    (names ?? []).filter((entry) => key(entry.name) === stored).map((entry) => entry.id),
  );
  return carriers.size === 1 ? types.find((type) => carriers.has(type.id)) : undefined;
}
